/**
 * Use Notes:
 *
 * HTML
 * <i class="ml-1 fa-solid fa-circle-question text-xs text-ia-blue cursor-pointer"
 *    data-br-popover
 *    data-br-popover-trigger="hover"
 *    data-br-popover-content="This set the current filter to do something with the filters so it can continue doing what it does after you're dong doing stuff."
 *    data-br-popover-class="max-w-[267px] bg-cms-superlight-gray border border-cms-light2-gray
 *     shadow-[0_0px_3px_0_rgba(0,0,0,0.07),0_2px_2px_0_rgba(0,0,0,0.04)] text-sm font-normal text-left leading-6"
 *    data-br-popover-content-class=""></i>
 *
 * Javascript
 * BRPopover.initAll(containerElement)
 * or
 * BRPopover.initAll() >> this uses document as the container element.
 */

import { addListener, htmlToElement } from "./support/utils";

class BRPopover
{
    static popInstances = new Map();
    constructor(popElement, options={})
    {
        if (popElement && BRPopover.getInstance(popElement) === undefined ) {
            this.popElement = popElement;

            const htmlOptions = {};
            const data = this.popElement.dataset;

            Object.keys(data).forEach(key => {
                if ( key.startsWith('brPopover') ) {
                    const optionName = key.replace('brPopover', '')
                                          .replace(/^\w/, c => c.toLowerCase() );
                    htmlOptions[optionName] = data[key];
                }
            })

            // Default options
            this.options = {
                vPlacement: "top",
                hPlacement: "center",
                trigger: 'hover',
                class: 'max-w-[267px] bg-white border border-slate-900 shadow-[0_0px_3px_0_rgba(0,0,0,0.07),0_2px_2px_0_rgba(0,0,0,0.04)] text-sm font-normal text-left leading-6',
                template: `<div class="opacity-0 z-30 transition-opacity duration-150 ease-in-out absolute top-0 left-0 
        block break-words bg-clip-padding no-underline underline-offset-auto normal-case tracking-normal 
        break-normal whitespace-normal"
        role="tooltip">
    <h3 data-br-popover-title class="text-slate-900 font-semibold py-2 px-4 mb-0 border-b border-slate-900 empty:hidden"></h3>
    <div data-br-popover-contents class="p-4 text-slate-900"></div>
</div>`,
                html: true,
                title: '',
                content: 'Please add data-br-popover-content attribute to your HTML popover element.',
                ...htmlOptions,
                ...options
            }

            this.init();
        }

    }

    init()
    {
        if (this.options.trigger !== 'none') {
            if (this.options.trigger === 'hover') {
                // Mouse events
                addListener('mouseenter', this.popElement, (evt) => this.show(evt));
                addListener('mouseleave', this.popElement, (evt) => this.hide(evt));

                // touch events
                addListener('touchstart', this.popElement, (event) => {
                    event.preventDefault();
                    this.show(event);
                });
                addListener('touchend', this.popElement, (evt) => this.hide(evt));
            }
            else if (this.options.trigger === 'click') {
                // Mouse events
                addListener('click', this.popElement, (evt) => this.show(evt));
                // addListener('mouseleave', this.popElement, (evt) => this.hide(evt));

                // touch events
                addListener('touchstart', this.popElement, (event) => {
                    event.preventDefault();
                    this.show(event);
                });
                addListener('touchend', this.popElement, (evt) => this.hide(evt));
            }
        }
    }

    setTitle(value)
    {
        this.options.title = value;
    }

    setContent(value)
    {
        this.options.content = value;
    }

    setTemplate(value)
    {
        this.options.template = value;
    }

    show(evt)
    {
        const callerElement = evt.currentTarget;
        this.newID = 'brpopover' + this.#generateRandomInteger();

        // Create the DOM element using the template.
        this.popOver = htmlToElement(this.options.template);
        // Add the options.class
        this.options.class.split(/\s+/).forEach((item) => {
            this.popOver.classList.add(item);
        });
        this.popOver.querySelector('[data-br-popover-title]').innerHTML = this.options.title;
        this.popOver.querySelector('[data-br-popover-contents]').innerHTML = this.options.content;
        this.popOver.setAttribute('id', this.newID);

        // Position the popOver
        this.#setPopoverLocation(callerElement);

        // set the caller's attributes
        this.popElement.setAttribute('aria-describedby', this.newID);

        // Finally, remove the opacity-0 class
        this.popOver.classList.remove('opacity-0');
    }

    #setPopoverLocation(callerElement)
    {
        // Calculate target's position relative to the document
        const callerRect = callerElement.getBoundingClientRect();
        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        document.body.appendChild(this.popOver);

        if (this.options.vPlacement === 'top') {
            this.popOver.style.top = `${scrollTop + callerRect.top - this.popOver.offsetHeight}px`; // Adjust as needed

            if (this.options.hPlacement === 'right') {
                this.popOver.style.left = `${ scrollLeft + callerRect.left + callerRect.width - this.popOver.offsetWidth }px`;
            }
            else if (this.options.hPlacement === 'left') {
                this.popOver.style.left = `${ scrollLeft + callerRect.left }px`;
            }
            else if (this.options.hPlacement === 'center') {
                this.popOver.style.left = `${ scrollLeft + callerRect.left + callerRect.width - (this.popOver.offsetWidth / 2) }px`;
            }
        }
        else if (this.options.vPlacement === 'bottom') {

        }
    }

    hide()
    {
        // Remove the element from the DOM.
        this.popOver.remove();
        this.popElement.removeAttribute('aria-describedby');
    }

    setListener(event, element, method, data=null, options=false)
    {
        addListener(event, element, method, data, options);

        // Check if the element already has listeners stored
        if (!BRPopover.eventListeners.has(element)) {
            BRPopover.eventListeners.set(element, new Map());
        }

        const listenersMap = BRPopover.eventListeners.get(element);

        // Use a combination of event type and method as the key for the inner map
        // This assumes we won't add the exact same function twice as a listener for the same event type on the same element
        const eventKey = `${event}_${method.name}`;

        listenersMap.set(eventKey, method);
    }

    #generateRandomInteger() {
        // Generate a number between 100000 and 999999, inclusive
        const min = 100000;
        const max = 999999;
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }


    static initAll(parentElement)
    {
        let container = parentElement || document;
        container.querySelectorAll('[data-br-popover]').forEach(popElement => {
            if (!BRPopover.popInstances.has(popElement)) {
                new BRPopover(popElement);
            }
        });
    }

    // Static method to access a widget instance from a DOM element
    static getInstance(popElement) {
        return BRPopover.popInstances.get(popElement);
    }
}

export default BRPopover;